import { Dispatch } from 'react';
import { createBrowserHistory } from 'history';

import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { IAction } from '../root/i-action';
import { IApiService } from '../../services/api-service';
import { IState } from '../root/state.interface';
import { AssetActionType } from './asset.enum';
import { assetBaseUrl } from './asset.constants';
import { assetIconCache } from '../../services/asset-icon-cache/asset-icon-cache';
import {
  IAssetTypeProperties, IAssetTypes, IFieldAsset,
} from './i-asset';
import {
  IAssetIconBatchEntry,
  IAssetIconBatchResponse,
  IImportJob,
  IImportJobList,
  IImportJobPlan,
  IImportProblemPage,
  IStagedUpload,
} from '../../../utils/asset-type-import/i-import-job';
import { downloadBlob } from '../../../utils/download-blob';
import { IFormFields } from '../../../components/lib/field-edit-form/field-edit-form';

// The mongo projection trails a write by ~1.5s on the local stack; this leaves a margin.
export const PROJECTION_CATCHUP_MS = 2500;

// Matches the page size useAssetsData asks for when the list mounts.
const DEFAULT_ASSET_TYPES_PAGE_SIZE = 10;

export interface IAssetActions {
  getAssetType(id: number): Promise<void>,
  getAssetTypes(limit?: number, skip?: number, sort?: string, filter?: string): Promise<void>,
  // eslint-disable-next-line
  addAssetType(assetTypes: any): Promise<void>,
  editAssetType(
    editableFields: Array<IFormFields>,
    assetFields: IAssetTypes,
    deletedFields: Array<number>,
    currentAssetID: string,
  ): Promise<void>,
  // eslint-disable-next-line
  updateAssetType(assetType: any, propertiesStatus: any): Promise<void>;
  deleteAssetType(propertiesId: Array<number>, id: number): Promise<void>,
  getAssetTypeProperties(): Promise<void>,
  addAssetTypeProperty(asset: IAssetTypes): Promise<void>,
  editAssetTypeProperty(assetTypeProperty: IAssetTypeProperties): Promise<void>,
  deleteAssetTypeProperty(deletedFields: Array<number>): Promise<void>,
  fetchAssetIconBlob(assetTypeCode: string): Promise<Blob | null>,
  uploadAssetIcon(assetTypeCode: string, file: Blob): Promise<void>,
  deleteAssetIcon(assetTypeCode: string): Promise<void>,
  fetchAssetTypePropertyNames(): Promise<string[]>,

  createUpload(fileName: string, totalBytes: number): Promise<IStagedUpload>,
  uploadPart(
    uploadId: string,
    partIndex: number,
    chunk: Blob,
    onUploadProgress?: (event: ProgressEvent) => void,
  ): Promise<void>,
  fetchUpload(uploadId: string): Promise<IStagedUpload>,
  completeUpload(uploadId: string): Promise<void>,
  discardUpload(uploadId: string): Promise<void>,

  startAssetTypeImportJob(
    uploadId: string, plan: IImportJobPlan, onDuplicate?: string,
  ): Promise<IImportJob>,
  confirmAssetTypeImportJob(jobId: string): Promise<IImportJob>,
  fetchImportJob(jobId: string): Promise<IImportJob>,
  listImportJobs(type?: string, limit?: number): Promise<IImportJob[]>,
  cancelImportJob(jobId: string): Promise<IImportJob>,
  fetchImportProblems(jobId: string, skip: number, limit: number): Promise<IImportProblemPage>,
  downloadImportProblems(jobId: string): Promise<void>,

  uploadAssetIconBatch(icons: IAssetIconBatchEntry[]): Promise<IAssetIconBatchResponse>,
}

export class AssetsActions implements IAssetActions {
  protected readonly dispatch: Dispatch<IAction>;

  private readonly apiService: IApiService;

  private readonly state: IState;

  public constructor(
    state: IState,
    apiService: IApiService,
    dispatch: Dispatch<IAction>,
  ) {
    this.dispatch = dispatch;
    this.apiService = apiService;
    this.state = state;
  }

  public getAssetType = async (id: number): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPE_REQUEST, payload: null });
      const payload = await this.apiService.get({
        url: `/asset-manager/api/assetType/${id}`,
      });
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPE_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPE_FAIL, payload: err });
    }
  };

  public getAssetTypes = async (
    limit?: number,
    skip?: number,
    sort?: string,
    filter?: string,
  ): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPES_LIST_REQUEST, payload: null });

      const payload = await this.apiService.get({
        url: '/asset-manager/api/assetTypes',
        params: {
          limit, skip, filter, sort,
        },
      });
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPES_LIST_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPES_LIST_FAIL, payload: err });
    }
  };

  public addAssetType = async (
    // eslint-disable-next-line
    assetType: any
  ): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.ADD_ASSET_TYPE_REQUEST, payload: null });
      const data = cloneDeep(assetType);
      delete data.properties;
      const payload: {id: number} = await this.apiService.post({
        url: `${assetBaseUrl}assetTypes`,
        data,
      });

      // eslint-disable-next-line
      assetType.properties.forEach(async (property: any) => {
        await this.apiService.post({
          url: `${assetBaseUrl}assetTypeProperties`,
          data: {
            name: property.name,
            value: property.value,
            assetType_id: payload.id,
          },
        });
      });

      createBrowserHistory().goBack();
      this.dispatch({ type: AssetActionType.ADD_ASSET_TYPE_SUCCESS, payload: assetType });
    } catch (err) {
      this.dispatch({ type: AssetActionType.ADD_ASSET_TYPE_FAIL, payload: err });
    }
  };

  // eslint-disable-next-line
  public updateAssetType = async (assetType: any, propertiesStatus: any): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_REQUEST, payload: null });

      const data = cloneDeep(assetType);
      delete data.properties;

      await this.apiService.put({
        url: `${assetBaseUrl}assetType/${assetType.id}`,
        data,
      });

      // eslint-disable-next-line
      assetType.properties.forEach(async (property: any) => {
        const status = propertiesStatus[property.id];
        if (status) {
          if (!isEqual(property, status.snapshot)) {
            // the property is modified so call update property service
            await this.apiService.put({
              url: `${assetBaseUrl}assetTypeProperty/${property.id}`,
              data: { name: property.name, value: property.value },
            });
          }
        } else {
          // no status, so it is a new property call service to add the property
          await this.apiService.post({
            url: `${assetBaseUrl}assetTypeProperties`,
            data: { assetType_id: assetType.id, name: property.name, value: property.value },
          });
        }
      });

      Object.keys(propertiesStatus).forEach(async (key) => {
        const status = propertiesStatus[key];
        if (status.removed) {
          await this.apiService.delete<null>({
            url: `${assetBaseUrl}assetTypeProperty/${status.snapshot.id}`,
          });
        }
      });

      const payload = await this.apiService.get({
        url: `/asset-manager/api/assetType/${assetType.id}`,
      });

      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_FAIL, payload: err });
    }
  };

  public editAssetType = async (
    editableFields: Array<IFormFields>,
    assetFields: IAssetTypes,
    deletedFields: Array<number>,
    currentAssetID: string,
  ): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_REQUEST, payload: null });
      const asset = {
        id: assetFields.id,
        tenant: assetFields.tenant,
        code: assetFields.code,
        description: assetFields.description,
      };
      let isAssetUpdated = false;
      editableFields.forEach(async (editableField) => {
        if (editableField.id && editableField.isEdited) {
          this.editAssetTypeProperty(
            { id: editableField.id, name: editableField.name, value: editableField.value },
          );
        }
        if (!editableField.id && !editableField.isStatic) {
          this.addAssetTypeProperty({
            name: editableField.name,
            value: editableField.value,
            assetType_id: Number(currentAssetID),
          });
        }
        if (!editableField.id && editableField.isEdited && !isAssetUpdated) {
          isAssetUpdated = true;
          await this.apiService.put({
            url: `${assetBaseUrl}assetType/${currentAssetID}`,
            data: asset,
          });
        }
      });
      if (deletedFields.length) {
        this.deleteAssetTypeProperty(deletedFields);
      }
      await this.getAssetTypes();
      createBrowserHistory().goBack();
      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_SUCCESS, payload: asset });
    } catch (err) {
      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_FAIL, payload: err });
    }
  };

  public deleteAssetType = async (propertiesId: Array<number>, id: number): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.DELETE_ASSET_TYPE_REQUEST, payload: null });

      if (propertiesId.length) {
        await Promise.all(propertiesId.map((propertyId) => (
          this.apiService.delete({
            url: `${assetBaseUrl}assetTypeProperty/${propertyId}`,
          })
        )));
      }

      await this.apiService.delete<null>({
        url: `${assetBaseUrl}assetType/${id}`,
      });

      createBrowserHistory().goBack();
      this.dispatch({ type: AssetActionType.DELETE_ASSET_TYPE_SUCCESS, payload: id });
      this.refreshAssetTypesAfterProjection();
    } catch (err) {
      this.dispatch({ type: AssetActionType.DELETE_ASSET_TYPE_FAIL, payload: err });
    }
  };

  // The list reads the mongo projection, so the page we just navigated back to still shows the
  // deleted row. Re-read once it has caught up.
  private refreshAssetTypesAfterProjection = (): void => {
    this.getAssetTypes(DEFAULT_ASSET_TYPES_PAGE_SIZE);
    setTimeout(
      () => this.getAssetTypes(DEFAULT_ASSET_TYPES_PAGE_SIZE),
      PROJECTION_CATCHUP_MS,
    );
  };

  public getAssetTypeProperties = async (): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPE_PROPERTIES_REQUEST, payload: null });
      const payload = await this.apiService.get({ url: `${assetBaseUrl}assetTypeProperties` });
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPE_PROPERTIES_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: AssetActionType.GET_ASSET_TYPE_PROPERTIES_FAIL, payload: err });
    }
  };

  public addAssetTypeProperty = async (assetTypeProperty: IAssetTypeProperties): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.ADD_ASSET_TYPE_PROPERTY_REQUEST, payload: null });
      const payload: IFieldAsset = await this.apiService.post({
        url: `${assetBaseUrl}assetTypeProperties`,
        data: assetTypeProperty,
      });
      this.dispatch({
        type: AssetActionType.ADD_ASSET_TYPE_PROPERTY_SUCCESS,
        payload: { ...assetTypeProperty, ...payload },
      });
    } catch (err) {
      this.dispatch({ type: AssetActionType.ADD_ASSET_TYPE_PROPERTY_FAIL, payload: err });
    }
  };

  public editAssetTypeProperty = async (assetTypeProperty: IAssetTypeProperties): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_PROPERTY_REQUEST, payload: null });
      await this.apiService.put({
        url: `${assetBaseUrl}assetTypeProperty/${assetTypeProperty.id}`,
        data: assetTypeProperty,
      });
      this.dispatch({
        type: AssetActionType.EDIT_ASSET_TYPE_PROPERTY_SUCCESS, payload: assetTypeProperty,
      });
    } catch (err) {
      this.dispatch({ type: AssetActionType.EDIT_ASSET_TYPE_PROPERTY_FAIL, payload: err });
    }
  };

  public deleteAssetTypeProperty = async (deletedFields: Array<number>): Promise<void> => {
    try {
      this.dispatch({ type: AssetActionType.DELETE_ASSET_TYPE_PROPERTY_REQUEST, payload: null });
      deletedFields.forEach(async (id) => {
        await this.apiService.delete<null>({
          url: `${assetBaseUrl}assetTypeProperty/${id}`,
        });
      });
      this.dispatch({
        type: AssetActionType.DELETE_ASSET_TYPE_PROPERTY_SUCCESS, payload: deletedFields,
      });
    } catch (err) {
      this.dispatch({ type: AssetActionType.DELETE_ASSET_TYPE_PROPERTY_FAIL, payload: err });
    }
  };

  public fetchAssetIconBlob = async (assetTypeCode: string): Promise<Blob | null> => {
    try {
      const blob = await this.apiService.get<Blob>({
        url: `${assetBaseUrl}assetIcon/${encodeURIComponent(assetTypeCode)}`,
        responseType: 'blob',
        silent: true,
      });
      return blob && blob.size > 0 ? blob : null;
    } catch {
      return null;
    }
  };

  public uploadAssetIcon = async (assetTypeCode: string, file: Blob): Promise<void> => {
    await this.apiService.put({
      url: `${assetBaseUrl}assetIcon/${encodeURIComponent(assetTypeCode)}`,
      headers: { 'Content-Type': file.type || 'image/png' },
      data: file,
    });
    assetIconCache.invalidate(assetTypeCode);
  };

  public deleteAssetIcon = async (assetTypeCode: string): Promise<void> => {
    await this.apiService.delete({
      url: `${assetBaseUrl}assetIcon/${encodeURIComponent(assetTypeCode)}`,
    });
    assetIconCache.invalidate(assetTypeCode);
  };

  /*
   *
   * AVP-891 — chunked upload, the server-side import job, icon batches
   *
   */

  public fetchAssetTypePropertyNames = async (): Promise<string[]> => {
    const response = await this.apiService.get<{ names?: string[] }>({
      url: `${assetBaseUrl}assetTypeProperties/names`,
      silent: true,
    });
    return response?.names ?? [];
  };

  public createUpload = async (fileName: string, totalBytes: number): Promise<IStagedUpload> => (
    this.apiService.post<Record<string, unknown>, IStagedUpload>({
      url: `${assetBaseUrl}uploads`,
      data: { fileName, totalBytes, purpose: 'assetTypes' },
    })
  );

  public uploadPart = async (
    uploadId: string,
    partIndex: number,
    chunk: Blob,
    onUploadProgress?: (event: ProgressEvent) => void,
  ): Promise<void> => {
    await this.apiService.put<Blob, void>({
      url: `${assetBaseUrl}uploads/${encodeURIComponent(uploadId)}/parts/${partIndex}`,
      headers: { 'Content-Type': 'application/octet-stream' },
      data: chunk,
      onUploadProgress,
      // a part is 4 MiB; well inside nginx's 60s even on a slow uplink
      timeout: 120000,
    });
  };

  public fetchUpload = async (uploadId: string): Promise<IStagedUpload> => (
    this.apiService.get<IStagedUpload>({
      url: `${assetBaseUrl}uploads/${encodeURIComponent(uploadId)}`,
      silent: true,
    })
  );

  public completeUpload = async (uploadId: string): Promise<void> => {
    await this.apiService.post<undefined, void>({
      url: `${assetBaseUrl}uploads/${encodeURIComponent(uploadId)}/complete`,
    });
  };

  public discardUpload = async (uploadId: string): Promise<void> => {
    // best effort: the server reaps abandoned uploads anyway
    try {
      await this.apiService.delete({
        url: `${assetBaseUrl}uploads/${encodeURIComponent(uploadId)}`,
        silent: true,
      });
    } catch {
      // nothing the user can do about it
    }
  };

  public startAssetTypeImportJob = async (
    uploadId: string,
    plan: IImportJobPlan,
    onDuplicate = 'skip',
  ): Promise<IImportJob> => (
    this.apiService.post<Record<string, unknown>, IImportJob>({
      url: `${assetBaseUrl}assetTypes/importJob`,
      data: { uploadId, plan, onDuplicate },
    })
  );

  public confirmAssetTypeImportJob = async (jobId: string): Promise<IImportJob> => (
    this.apiService.post<undefined, IImportJob>({
      url: `${assetBaseUrl}assetTypes/importJob/${encodeURIComponent(jobId)}/start`,
      params: { confirm: true },
    })
  );

  public fetchImportJob = async (jobId: string): Promise<IImportJob> => (
    this.apiService.get<IImportJob>({
      url: `${assetBaseUrl}importJob/${encodeURIComponent(jobId)}`,
      // polled every couple of seconds; a blip must not raise a global toast
      silent: true,
      timeout: 10000,
    })
  );

  public listImportJobs = async (type = 'assetTypes', limit = 5): Promise<IImportJob[]> => {
    const response = await this.apiService.get<IImportJobList>({
      url: `${assetBaseUrl}importJob`,
      params: { type, limit },
      silent: true,
    });
    return response?.jobs ?? [];
  };

  public cancelImportJob = async (jobId: string): Promise<IImportJob> => (
    this.apiService.post<undefined, IImportJob>({
      url: `${assetBaseUrl}importJob/${encodeURIComponent(jobId)}/cancel`,
    })
  );

  public fetchImportProblems = async (
    jobId: string, skip: number, limit: number,
  ): Promise<IImportProblemPage> => (
    this.apiService.get<IImportProblemPage>({
      url: `${assetBaseUrl}importJob/${encodeURIComponent(jobId)}/errors`,
      params: { skip, limit },
      silent: true,
    })
  );

  /**
   * Fetched with the bearer token and saved, never linked to with an anchor:
   * the JWT is in localStorage, not a cookie, so a plain navigation would
   * reach the endpoint unauthenticated and come back 401.
   */
  public downloadImportProblems = async (jobId: string): Promise<void> => {
    const blob = await this.apiService.get<Blob>({
      url: `${assetBaseUrl}importJob/${encodeURIComponent(jobId)}/errors.csv`,
      responseType: 'blob',
    });
    downloadBlob(blob, `import-problems-${jobId}.csv`);
  };

  public uploadAssetIconBatch = async (
    icons: IAssetIconBatchEntry[],
  ): Promise<IAssetIconBatchResponse> => {
    const response = await this.apiService.post<
    { icons: IAssetIconBatchEntry[] }, IAssetIconBatchResponse
    >({
      url: `${assetBaseUrl}assetIcons/batch`,
      data: { icons },
    });
    assetIconCache.clear();
    return response;
  };
}
